import { inject, Injectable, InjectionToken } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, merge } from 'rxjs';
import { AiChatInternalPromptService } from '@panaya/ai-chat/domain';
import { injectAiChatEventNotifier } from '../conversation-tools/ai-chat-events.provider';
import { AiChatThreadService } from '../conversation-tools/ai-chat-thread.service';
import { CanvasFrameStore } from './canvas-frame.store';

/** Anything that must clear when a thread is initialized or closed registers here (the draft store does). */
export const CANVAS_RESETTABLES = new InjectionToken<{ reset(): void }[]>('__CANVAS_RESETTABLES__', {
    factory: (): { reset(): void }[] => []
});

/**
 * Clears canvas state when the thread changes, and ends a turn-scoped status when the user sends the next prompt.
 * Instantiated once by `provideCanvasFrame()`.
 */
@Injectable({ providedIn: 'root' })
export class CanvasResetService {
    private readonly frameStore = inject(CanvasFrameStore);
    private readonly resettables = inject(CANVAS_RESETTABLES, { optional: true }) ?? [];

    constructor() {
        // Restore switches to another thread too, so a canvas of the previous thread must not survive it.
        inject(AiChatThreadService)
            .id.pipe(
                filter(({ source }) => source === 'init' || source === 'close' || source === 'restore'),
                takeUntilDestroyed()
            )
            .subscribe(() => this.resetAll());

        // `awaiting` waits for the user's answer, so sending one ends it.
        merge(injectAiChatEventNotifier().promptRequest, inject(AiChatInternalPromptService).internalPrompt$)
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.frameStore.clearTurnStatus());
    }

    resetAll(): void {
        this.frameStore.reset();
        this.resettables.forEach((resettable) => resettable.reset());
    }
}
